<template>
  <div>
    <b-card v-if="isLoading">
      <div class="text-center">
        <b-spinner variant="primary" />
        <p class="mt-2">Cargando...</p>
      </div>
    </b-card>

    <b-card v-else-if="!gap">
      <b-alert variant="danger" show>
        No se encontró la evaluación de brecha
      </b-alert>
      <b-button variant="outline-secondary" @click="handleCancel">
        Volver
      </b-button>
    </b-card>

    <b-card v-else>
      <b-card-title>
        <feather-icon icon="EditIcon" size="20" class="mr-50" />
        Editar Evaluación de Brecha
      </b-card-title>

      <gap-form
        :form-data="formData"
        :is-edit="true"
        @submit="handleSubmit"
        @cancel="handleCancel"
      />
    </b-card>
  </div>
</template>

<script>
import { ref, onMounted } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import { BSpinner, BCardTitle } from 'bootstrap-vue'
import GapForm from '../shared/GapForm.vue'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useNotifications from '@/composables/useNotifications'
import useAuditLog from '@/composables/useAuditLog'
import { hasPermission } from '@/composables/usePermission'
import i18n from '@/libs/i18n'
import store from '@/store'

export default {
  name: 'GapEdit',
  components: {
    GapForm,
    BSpinner,
    BCardTitle,
  },
  setup() {
    const { router, route } = useRouter()
    const { getItemsAggregate, updateItem } = useLambda()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { addAuditEntry } = useAuditLog()
    const { worker_id: userId, client } = store.state?.userStore?.userData

    const isLoading = ref(true)
    const gap = ref(null)
    const originalData = ref(null)
    const formData = ref({})

    const fetchGap = async () => {
      try {
        const gapId = route.value.params.id

        const pipeline = [
          { $match: { _id: oid(gapId), client_id: oid(client.$oid), deleted: { $ne: true } } },
          {
            $lookup: {
              from: 'gapObligation',
              localField: 'obligation_id',
              foreignField: '_id',
              as: 'obligation',
            }
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'responsible_id',
              foreignField: '_id',
              as: 'responsible',
            }
          },
          {
            $addFields: {
              obligation: { $arrayElemAt: ['$obligation', 0] },
              responsible: { $arrayElemAt: ['$responsible', 0] },
            }
          }
        ]

        const result = await getItemsAggregate({
          collection: 'gap',
          pipeline,
        })

        if (result && result.length > 0) {
          gap.value = result[0]

          // Inicializar history si no existe (documentos legacy)
          if (!gap.value.history) {
            gap.value.history = []
          }

          formData.value = {
            obligation_id: gap.value.obligation_id,
            status: gap.value.status || 'No implementado',
            evidence: gap.value.evidence || '',
            gapDescription: gap.value.gapDescription || '',
            actionPlan: gap.value.actionPlan || '',
            responsible_id: gap.value.responsible_id,
            deadline: gap.value.deadline || null,
            planStatus: gap.value.planStatus || 'Pendiente',
            history: gap.value.history || [],
          }

          // Store original data for change detection
          originalData.value = JSON.parse(JSON.stringify(gap.value))
        }
      } catch (error) {
        console.log(error)
        showErrorMessage('Error al cargar evaluación de brecha')
      } finally {
        isLoading.value = false
      }
    }

    const handleSubmit = async (data) => {
      if (!hasPermission('privacy-gap', 'edit')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }
      try {
        const gapId = route.value.params.id

        const payload = {
          ...data,
          obligation_id: oid(data.obligation_id),
          responsible_id: data.responsible_id ? oid(data.responsible_id) : null,
          deadline: data.deadline ? date(data.deadline) : null,
        }

        // Remove history from $set to avoid overwriting
        delete payload.history

        const query = { _id: oid(gapId), client_id: oid(client.$oid) }
        const action = { $set: payload }

        await updateItem({
          collection: 'gap',
          query,
          action,
        })

        // Add audit log entry (sistema unificado)
        await addAuditEntry({
          collection: 'gap',
          documentId: oid(gapId),
          originalData: originalData.value,
          updatedData: payload,
        })

        showSuccessMessage('Evaluación de brecha actualizada correctamente')
        router.push({ name: 'privacy-gap-list' })
      } catch (error) {
        console.log(error)
        showErrorMessage('Error al actualizar evaluación de brecha')
      }
    }

    const handleCancel = () => {
      router.push({ name: 'privacy-gap-list' })
    }

    onMounted(() => {
      fetchGap()
    })

    return {
      isLoading,
      gap,
      formData,
      handleSubmit,
      handleCancel,
    }
  },
}
</script>
